import React, {useCallback, useRef, useState} from 'react';
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropperProps {
  aspect?: number; // default enforced below — pass 2 for a 2:1 banner
  circularCrop?: boolean;
  onCropComplete: (croppedImage: File) => void;
  modalTitle?: string;
  cropButtonText?: string;
  cancelButtonText?: string;
  onOpen?: () => void;
  outputWidth?: number;
  outputHeight?: number;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  aspect = 1, // hard default: 2:1, matches SliderItem container
  circularCrop = false,
  onCropComplete,
  modalTitle = 'برش تصویر',
  cropButtonText = 'تایید',
  cancelButtonText = 'انصراف',
  onOpen,
  outputWidth = 400,
  outputHeight = 400,
}) => {
  const [imgSrc, setImgSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setOriginalFile(file);
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '');
        setIsModalOpen(true);
      });
      reader.readAsDataURL(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const {width, height} = e.currentTarget;

    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        width,
        height,
      ),
      width,
      height,
    );

    setCrop(initialCrop);
    setCompletedCrop({
      unit: 'px',
      x: (initialCrop.x / 100) * width,
      y: (initialCrop.y / 100) * height,
      width: (initialCrop.width / 100) * width,
      height: (initialCrop.height / 100) * height,
    });
  };

  const handleCropComplete = useCallback((crop: PixelCrop) => {
    setCompletedCrop(crop);
  }, []);

  const getCroppedImg = async (): Promise<File | null> => {
    if (!completedCrop || !imgRef.current || !originalFile) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      outputWidth,
      outputHeight,
    );

    return new Promise(resolve => {
      canvas.toBlob(
        blob => {
          if (blob) {
            const fileExtension = originalFile.name.split('.').pop() || 'jpg';
            const fileName = `cropped-${Date.now()}.${fileExtension}`;
            resolve(
              new File([blob], fileName, {
                type: blob.type,
                lastModified: Date.now(),
              }),
            );
          } else {
            resolve(null);
          }
        },
        'image/jpeg',
        0.9,
      );
    });
  };

  const handleCrop = async () => {
    const croppedImage = await getCroppedImg();
    if (croppedImage) {
      onCropComplete(croppedImage);
      closeModal();
    }
  };

  const triggerFileSelect = () => {
    onOpen?.();
    fileInputRef.current?.click();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
    setOriginalFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <label
        onClick={triggerFileSelect}
        className="flex gap-1 items-center justify-center text-md w-20 h-20 rounded-xl border-[2px] border-dashed text-gray-700 cursor-pointer"
      >
        <i className="text-4xl text-gray-500 icon-namePlus-4" />
      </label>

      <input
        type="file"
        accept="image/*"
        onChange={onSelectFile}
        ref={fileInputRef}
        className="hidden"
      />

      {/* Modal */}
      {isModalOpen && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
              <span className="text-lg font-medium text-gray-900">
                {modalTitle}
              </span>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-500"
                aria-label="Close"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex justify-center items-center p-4">
              {imgSrc && (
                <ReactCrop
                  crop={crop}
                  onChange={c => setCrop(c)}
                  onComplete={handleCropComplete}
                  aspect={aspect}
                  circularCrop={circularCrop}
                  className="max-h-[60vh]"
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imgSrc}
                    onLoad={onImageLoad}
                    className="max-w-full max-h-[60vh]"
                  />
                </ReactCrop>
              )}
            </div>

            <div className="flex gap-2 justify-center px-4 py-3 w-full border-t border-gray-200">
              <button
                onClick={handleCrop}
                type="button"
                className="px-4 py-2 w-1/2 font-medium text-white rounded transition-colors bg-farm-shop-500"
                disabled={!completedCrop}
              >
                {cropButtonText}
              </button>
              <button
                onClick={closeModal}
                className="border-[1px] border-red-500 text-red-500 w-1/2 font-medium py-2 px-4 rounded transition-colors"
              >
                {cancelButtonText}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageCropper;
